<template>
  <div class="epptal">
    <EmployeeTimeline
      title="Pay Plans timeline"
      subtitle="Primary position assignments, pay range versions, and pay plans"
      fill-width
      :assignments="timelineAssignments"
      :pay-range-versions="payRangeVersions"
      :pay-plans="timelineBars"
      :today="today"
      :loading="loading"
      :selected-pay-plan-id="selectedVersion"
      :hovered-pay-plan-id="hoveredVersion"
      :pay-range-versions-selectable="payRangeVersionsSelectable"
      :selected-pay-range-version-id="selectedPayRangeVersion"
      :selectable-assignments="barNavigation"
      @bar-select="handleBarSelect"
      @pay-range-version-select="handlePayRangeVersionSelect"
      @assignment-select="handleAssignmentSelect"
      @bar-hover="handleBarHover"
    />

    <div class="epptal__list">
      <header class="epptal__list-header">
        <div class="epptal__list-heading">
          <h2 class="epptal__list-title">Pay Plans</h2>
          <p class="epptal__list-subtitle">Pay plan versions and proposals</p>
        </div>
        <div v-if="showProposeChanges" class="epptal__list-actions">
          <EXTooltip
            v-if="proposeChangesDisabled"
            title="Edit, delete, or void the open proposal first"
            placement="top"
          >
            <ChangePayCardSmallButtons
              button="propose-changes"
              :disabled="true"
              @click="onProposeChanges"
            />
          </EXTooltip>
          <ChangePayCardSmallButtons
            v-else
            button="propose-changes"
            @click="onProposeChanges"
          />
        </div>
      </header>

      <div
        v-if="showCoverageGap && !loading"
        class="epptal__coverage-gap"
        role="status"
      >
        <div class="epptal__coverage-gap-icon" aria-hidden="true">⚠️</div>
        <div class="epptal__coverage-gap-text">
          No pay plan in force from {{ coverageGapDate }}. Create a pay plan proposal to cover this period.
        </div>
      </div>

      <div v-if="loading" class="epptal__skeleton">
        <div v-for="i in 3" :key="`skeleton-${i}`" class="epptal__skeleton-row">
          <div class="epptal__skeleton-badge epptal__skeleton-block"></div>
          <div class="epptal__skeleton-amount epptal__skeleton-block"></div>
          <div class="epptal__skeleton-date epptal__skeleton-block"></div>
          <div class="epptal__skeleton-status epptal__skeleton-block"></div>
          <div class="epptal__skeleton-state epptal__skeleton-block"></div>
          <div class="epptal__skeleton-icon epptal__skeleton-block"></div>
          <div class="epptal__skeleton-icon epptal__skeleton-block"></div>
        </div>
      </div>

      <div v-else-if="sortedVersions.length === 0" class="epptal__empty">
        <h3 class="epptal__empty-title">No pay plans yet</h3>
        <p class="epptal__empty-text">
          This employee has no pay plan or proposal. Create one to set their pay.
        </p>
        <button
          type="button"
          class="epptal__empty-btn"
          @click="$emit('create-pay-plan')"
        >
          Add Pay Plan
        </button>
      </div>

      <div v-else class="epptal__rows">
        <PayPlanRowCard
          v-for="version in sortedVersions"
          :key="version.id"
          v-bind="rowPropsFor(version)"
          show-open-ended-dash
          :selected="selectedVersion === version.id"
          :interaction="rowInteractionFor(version)"
          @select="handleVersionSelect(version)"
          @hover="(payload) => handleRowHoverEvent(version, payload)"
          @menu-action="(item) => handleMenuAction(version, item?.value ?? item)"
          @open-chatter="handleOpenChatter(version)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

import ChangePayCardSmallButtons from "@components/ChangePayCardSmallButtons/ChangePayCardSmallButtons.vue";
import EmployeeTimeline from "@components/EmployeeTimeline/EmployeeTimeline.vue";
import PayPlanRowCard from "@components/PayPlanRowCard/PayPlanRowCard.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import {
  computePayPlanPeriodState,
  coversDate,
  hasFuturePayPlan,
  mapAssignmentsToTimelineBars,
  mapPayPlanVersionsToTimelineBars,
  mapPayPlanVersionToRowProps,
  markNavigableRecords,
  resolveAssignmentPositionId,
  sortPayPlanVersionsNewestFirst,
} from "./employeePayPlansTimelineUtils";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  assignments: {
    type: Array,
    default: () => [],
  },
  payRangeVersions: {
    type: Array,
    default: () => [],
  },
  payPlanVersions: {
    type: Array,
    default: () => [],
  },
  today: {
    type: String,
    default: () => new Date().toISOString().split("T")[0],
  },
  selectedVersion: {
    type: [String, Number],
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** Pay range version bars open their drawer (off keeps them display-only). */
  payRangeVersionsSelectable: {
    type: Boolean,
    default: false,
  },
  selectedPayRangeVersion: {
    type: [String, Number],
    default: null,
  },
  /** Occupies Positions bars are clickable (emit assignment-select). */
  barNavigation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "assignment-select",
  "version-select",
  "version-hover",
  "pay-range-version-select",
  "open-chatter",
  "menu-action",
  "create-pay-plan",
  "create-proposal",
  "propose-changes",
]);

const hoveredVersion = ref(null);

const timelineAssignments = computed(() =>
  mapAssignmentsToTimelineBars(
    props.barNavigation
      ? markNavigableRecords(props.assignments, resolveAssignmentPositionId)
      : props.assignments,
    props.today
  )
);

const sortedVersions = computed(() => sortPayPlanVersionsNewestFirst(props.payPlanVersions));

const timelineBars = computed(() =>
  mapPayPlanVersionsToTimelineBars(props.payPlanVersions, props.today)
);

const proposeChangesDisabled = computed(() =>
  hasFuturePayPlan(props.payPlanVersions, props.today)
);

const showProposeChanges = computed(
  () => !props.loading && sortedVersions.value.length > 0
);

const showCoverageGap = computed(() => {
  if (!props.payPlanVersions?.length) return false;

  const todayDate = new Date(props.today);
  const hasActivePosition = (props.assignments || []).some((a) => coversDate(a, todayDate));
  if (!hasActivePosition) return false;

  const hasActiveCoverage = props.payPlanVersions.some(
    (v) =>
      (v.status === "In Force" || v.status === "Proposed") && coversDate(v, todayDate)
  );

  return !hasActiveCoverage;
});

const coverageGapDate = computed(() => {
  const lastLapsed = sortedVersions.value.find(
    (v) => computePayPlanPeriodState(v, props.today) === "Lapsed"
  );

  if (lastLapsed?.effectiveTo) {
    const gapDate = new Date(lastLapsed.effectiveTo);
    gapDate.setDate(gapDate.getDate() + 1);
    return formatFairPayDate(gapDate);
  }

  return formatFairPayDate(props.today);
});

function onProposeChanges() {
  if (proposeChangesDisabled.value) return;
  emit("propose-changes");
}

function rowPropsFor(version) {
  return mapPayPlanVersionToRowProps(version, props.today);
}

function rowInteractionFor(version) {
  return hoveredVersion.value === version.id ? "hover" : "default";
}

function handleVersionSelect(version) {
  emit("version-select", version);
}

function handleRowHoverEvent(version, payload) {
  if (payload?.hovered) {
    hoveredVersion.value = version.id;
    emit("version-hover", version);
    return;
  }
  hoveredVersion.value = null;
  emit("version-hover", null);
}

function resolveVersionById(barId) {
  if (barId == null) return null;
  return (
    (props.payPlanVersions || []).find((v) => String(v.id) === String(barId)) || null
  );
}

function handleBarSelect(barId) {
  emit("version-select", resolveVersionById(barId));
}

function handlePayRangeVersionSelect(versionId) {
  emit("pay-range-version-select", versionId);
}

function handleBarHover(barId) {
  const version = resolveVersionById(barId);
  hoveredVersion.value = version?.id ?? null;
  emit("version-hover", version);
}

function findRecordById(records, id) {
  if (id == null) return null;
  return (records || []).find((record) => String(record.id) === String(id)) || null;
}

function handleAssignmentSelect(barId) {
  const assignment = findRecordById(props.assignments, barId);
  if (assignment) emit("assignment-select", assignment);
}

function handleMenuAction(version, action) {
  emit("menu-action", version, action);
}

function handleOpenChatter(version) {
  emit("open-chatter", version);
}
</script>

<style scoped>
.epptal {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.epptal__list {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #eaecf0;
}

.epptal__list-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.epptal__list-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.epptal__list-title {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
  margin: 0;
}

.epptal__list-subtitle {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.epptal__list-actions {
  flex-shrink: 0;
}

.epptal__coverage-gap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fffaeb;
  border: 1px solid #fedf89;
}

.epptal__coverage-gap-icon {
  font-size: 20px;
}

.epptal__coverage-gap-text {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #b54708;
  flex: 1;
}

.epptal__skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.epptal__skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 78px;
  padding: 14px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #ffffff;
}

.epptal__skeleton-block {
  border-radius: 6px;
  background: #f2f4f7;
  animation: epptal-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.epptal__skeleton-badge {
  width: 80px;
  height: 24px;
}

.epptal__skeleton-amount {
  width: 56px;
  height: 32px;
  border-radius: 4px;
}

.epptal__skeleton-date {
  flex: 1;
  min-width: 120px;
  height: 20px;
  border-radius: 4px;
}

.epptal__skeleton-status {
  width: 72px;
  height: 24px;
}

.epptal__skeleton-state {
  width: 72px;
  height: 24px;
}

.epptal__skeleton-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

@keyframes epptal-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.epptal__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  text-align: center;
}

.epptal__empty-title {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
  margin: 0;
}

.epptal__empty-text {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  margin: 0;
}

.epptal__empty-btn {
  margin-top: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #7f56d9;
  background: #7f56d9;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.epptal__empty-btn:hover {
  background: #6941c6;
  border-color: #6941c6;
}

.epptal__rows {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
}
</style>
